import api from "../api";
import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import AuthCard from "./authCard";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import loginSchema from "./validation";
import UserContext from "./userContext";
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";

function LoginForm() {
  const methods = useForm({
    resolver: yupResolver(loginSchema),
  });
  const { errors, handleSubmit, register } = methods;
  const [loginStatus, setLoginStatus] = useState("");
  // Redirige vers la page première connexion
  const handleFirstConnexion = () => {
    navigate("/First-login");
  };
  const [id, setId] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const context = useContext(UserContext);
  const { setUser, setIsLoggedIn, setRole, setFirstName } = context;
  const [loading, setLoading] = useState(true);
  const [new_sk, setNew_sk] = useState(null);

  useEffect(() => {
    api.get("/events/key", { withCredentials: true })
      .then((response) => {
        setNew_sk(response.data.key);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error:", error);
      }
      )
  }, []);

  const navigate = useNavigate();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (newPassword && confirmPassword) {
      setPasswordMatch(newPassword === confirmPassword);
    }
  }, [newPassword, confirmPassword]);

  const onSubmit = async (data) => {
    try {
      const logResponse = await api.post("/events/login", data, { withCredentials: true });
      
      if (logResponse.data.success) {
        if (logResponse.data.requirePasswordChange) {
          setLoginStatus(logResponse.data.message);
          navigate(`/PasswordUpdate/${logResponse.data.resetToken}`);
          return;
        }
        
        const secretKeyResponse = await api.get("/events/key", { withCredentials: true });
        const encryptedUser = CryptoJS.AES.encrypt(JSON.stringify(logResponse.data), secretKeyResponse.data.key).toString();
        
        Cookies.set('user', encryptedUser, { 
          secure: false,
          sameSite: 'lax',
        });
        
        localStorage.setItem('jwtToken', logResponse.data.token);
                
        setUser(logResponse.data);
        setRole(logResponse.data.Role__c);
        setFirstName(logResponse.data.FirstName__c);
        setIsLoggedIn(true);
        
        navigate("/SalonsList");
      } else {
        setLoginStatus(logResponse.data.error || logResponse.data.message);
      }
    } catch (err) {
      // Le backend répond 400/500 avec un message précis (compte non
      // initialisé, identifiants invalides…) : on l'affiche tel quel.
      setLoginStatus(err.response?.data?.error || "Vérifiez votre email et/ou votre mot de passe");
      console.error(err);
    }
  };

  const handleChangePassword = async () => {
    const userData = {
      Id: id,
      Email__c: methods.getValues("Email__c"),
      newPassword: newPassword,
      oldPassword: oldPassword,
    };
    if (newPassword !== confirmPassword) {
      console.log("Les mots de passe ne correspondent pas");
      setPasswordMatch(false);
      return;
    }
    try {
      const response = await api.post(
        "/events/changePassword",
        userData
      );
      if (response.data.success) {
        console.log("Mot de passe modifié avec succès.");

        const loginData = {
          Email__c: userData.Email__c,
          Password__c: userData.newPassword,
        };
        const loginResponse = await api.post("/events/login", loginData, { withCredentials: true },);
        if (loginResponse.data.success) {
          localStorage.setItem('jwtToken', loginResponse.data.token);

          setUser(loginResponse.data);
          setRole(loginResponse.data.Role__c);
          setFirstName(loginResponse.data.FirstName__c);
          setIsLoggedIn(true);
          const user = loginResponse.data;
          const ciphertext = CryptoJS.AES.encrypt(
            JSON.stringify(user),
            new_sk
          ).toString();
          Cookies.set(
            "user",
            ciphertext,
            { secure: true, sameSite: "strict" },
            { expires: 1 }
          );
          console.log("Connexion réussie après le changement de mot de passe.");
          navigate("/SalonsList");
        } else {
          console.log("La connexion a échoué après le changement de mot de passe.");
        }
      } else {
        console.log("Le mot de passe n'a pas pu être mis à jour");
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <AuthCard>
        <Typography variant="h5" align="center" gutterBottom sx={{ mb: 3 }}>
          Connexion
        </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    name="Email__c"
                    type="email"
                    label="Email"
                    {...register("Email__c")}
                    error={!!errors?.Email__c}
                    helperText={errors?.Email__c?.message}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Password__c"
                    type={showPassword ? "text" : "password"}
                    label="Mot de passe"
                    {...register("Password__c")}
                    error={!!errors?.Password__c}
                    helperText={errors?.Password__c?.message}
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" fullWidth>
                    Connexion
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="text" size="small" onClick={() => navigate("/ForgotPassword")} fullWidth>
                    Mot de passe oublié
                  </Button>
                  <Button variant="text" size="small" onClick={handleFirstConnexion} fullWidth>
                    Première connexion / Initialiser mon compte
                  </Button>
                </Grid>
              </Grid>
            </form>
        {loginStatus && (
          <Typography align="center" color="error" sx={{ mt: 2 }}>
            {loginStatus}
          </Typography>
        )}
      </AuthCard>
    </FormProvider>
  );
}

export default LoginForm;
